import runtimeEndpointDefaults from './runtime-endpoints.json';
function normalizeServiceBaseUrl(value, environmentName) {
    if (typeof value !== 'string' || !value.trim())
        return null;
    try {
        const parsed = new URL(value.trim());
        const validProtocol = parsed.protocol === 'http:' || parsed.protocol === 'https:';
        const secureForEnvironment = environmentName === 'development' || parsed.protocol === 'https:';
        if (!validProtocol || !secureForEnvironment || parsed.username || parsed.password || parsed.search || parsed.hash) {
            return null;
        }
        parsed.pathname = parsed.pathname.endsWith('/') ? parsed.pathname : `${parsed.pathname}/`;
        return parsed.toString();
    }
    catch {
        return null;
    }
}
function readEnvironmentCandidate(value) {
    if (!value || typeof value !== 'object') {
        return {};
    }
    return value;
}
// Lightweight runtime validation. Pulling zod into the public widget added a large
// dependency for three URL checks, so keep this schema-shaped surface local.
export const envSchema = {
    parse(value) {
        const parsed = parseEnvironment(value);
        if (!parsed.success) {
            throw ('error' in parsed ? parsed.error : new Error('Invalid environment configuration'));
        }
        return parsed.data;
    },
    safeParse(value) {
        return parseEnvironment(value);
    },
};
function parseEnvironment(value) {
    const candidate = readEnvironmentCandidate(value);
    const environmentName = normalizeEnvironment(candidate.ENVIRONMENT);
    const failures = [];
    const webUrl = normalizeServiceBaseUrl(candidate.SHINE_AI_WEB_URL, environmentName);
    const apiUrl = normalizeServiceBaseUrl(candidate.SHINE_AI_API_URL, environmentName);
    const frontendUrl = normalizeServiceBaseUrl(candidate.SHINE_AI_FRONTEND_URL, environmentName);
    if (!webUrl) {
        failures.push('SHINE_AI_WEB_URL must be a valid service base URL (HTTPS outside development)');
    }
    if (!apiUrl) {
        failures.push('SHINE_AI_API_URL must be a valid service base URL (HTTPS outside development)');
    }
    if (!frontendUrl) {
        failures.push('SHINE_AI_FRONTEND_URL must be a valid service base URL (HTTPS outside development)');
    }
    if (failures.length > 0) {
        return { success: false, error: new Error(failures.join('; ')) };
    }
    return {
        success: true,
        data: {
            ENVIRONMENT: environmentName,
            SHINE_AI_WEB_URL: webUrl,
            SHINE_AI_API_URL: apiUrl,
            SHINE_AI_FRONTEND_URL: frontendUrl,
        },
    };
}
function normalizeEnvironment(env) {
    const value = (env ?? '').toString().toLowerCase();
    switch (value) {
        case 'development':
        case 'beta':
        case 'staging':
        case 'production':
            return value;
        default:
            return 'development';
    }
}
const PRODUCTION_WEB_URL = 'https://web.shine-ai.intuvio.com/';
const PRODUCTION_API_URL = 'https://api.shine-ai.intuvio.com/';
const PRODUCTION_ENVIRONMENT = 'production';
const DEFAULT_FRONTEND_URL = 'https://shine-ai.intuvio.com/';
const SCRIPT_SRC_HINTS = ['shine-ai', 'shine.frontend', 'generic/', 'generic.js', 'chatbot/', 'chatbot.js'];
const endpointDefaults = runtimeEndpointDefaults;
export function determineFallbackEnvironment(env) {
    if (env === 'development') {
        return PRODUCTION_ENVIRONMENT;
    }
    return env;
}
const buildEnvironment = normalizeEnvironment(process.env.ENVIRONMENT);
const fallbackEnvironment = determineFallbackEnvironment(buildEnvironment);
function getDefaultEndpoints(env) {
    return endpointDefaults[env] || endpointDefaults.production;
}
const fallbackDefaults = getDefaultEndpoints(fallbackEnvironment);
const fallbackWebUrl = fallbackDefaults.SHINE_AI_WEB_URL || PRODUCTION_WEB_URL;
const fallbackApiUrl = fallbackDefaults.SHINE_AI_API_URL || PRODUCTION_API_URL;
export function orderConfigOrigins(configuredOrigin, scriptOrigins = [], fallbackOrigin) {
    const ordered = [];
    const seen = new Set();
    const addOrigin = (origin) => {
        if (!origin)
            return;
        const normalized = origin.trim();
        if (!normalized || seen.has(normalized))
            return;
        seen.add(normalized);
        ordered.push(normalized);
    };
    scriptOrigins.forEach(addOrigin);
    addOrigin(configuredOrigin);
    addOrigin(fallbackOrigin);
    return ordered;
}
function ensureTrailingSlash(value) {
    if (!value) {
        return value;
    }
    return value.endsWith('/') ? value : `${value}/`;
}
function normalizeOriginFromSrc(src) {
    if (!src) {
        return null;
    }
    const baseHref = typeof window !== 'undefined' && window.location?.href ? window.location.href : 'http://localhost/';
    try {
        const parsed = new URL(src, baseHref);
        return ensureTrailingSlash(parsed.origin);
    }
    catch {
        return null;
    }
}
function collectScriptOrigins() {
    const origins = [];
    const seen = new Set();
    const addOrigin = (src) => {
        const origin = normalizeOriginFromSrc(src);
        if (origin && !seen.has(origin)) {
            seen.add(origin);
            origins.push(origin);
        }
    };
    if (typeof window !== 'undefined' && window.SHINE_WIDGET_SCRIPT_SRC) {
        addOrigin(window.SHINE_WIDGET_SCRIPT_SRC);
    }
    if (typeof document !== 'undefined') {
        const currentScript = document.currentScript;
        if (currentScript?.src) {
            addOrigin(currentScript.src);
            if (typeof window !== 'undefined' && !window.SHINE_WIDGET_SCRIPT_SRC) {
                window.SHINE_WIDGET_SCRIPT_SRC = currentScript.src;
            }
        }
        const scripts = Array.from(document.querySelectorAll('script[src]'));
        scripts.forEach(script => {
            const src = script.getAttribute('src');
            if (!src)
                return;
            if (SCRIPT_SRC_HINTS.some(hint => src.includes(hint))) {
                addOrigin(src);
            }
        });
    }
    return origins;
}
const runtimeScriptOrigins = collectScriptOrigins();
// Configuration state
let configState = {
    isLoaded: false,
    isLoading: false,
    loadPromise: null,
    config: null
};
// Function to get the frontend URL (compiled in at build time)
function getFrontendUrl() {
    const configuredOrigin = ensureTrailingSlash(process.env.SHINE_AI_FRONTEND_URL || '');
    const fallbackOrigin = ensureTrailingSlash(DEFAULT_FRONTEND_URL);
    const orderedOrigins = orderConfigOrigins(configuredOrigin, runtimeScriptOrigins, fallbackOrigin);
    const selectedOrigin = orderedOrigins[0] || fallbackOrigin;
    return ensureTrailingSlash(selectedOrigin);
}
function getConfigUrl() {
    const override = process.env.SHINE_RUNTIME_CONFIG_URL;
    if (override) {
        return override;
    }
    const frontendUrl = getFrontendUrl();
    try {
        return new URL('config.js', frontendUrl).toString();
    }
    catch {
        return `${frontendUrl.replace(/\/$/, '')}/config.js`;
    }
}
const CONFIG_SCRIPT_ATTR = 'data-shine-runtime-config';
const CONFIG_LOAD_TIMEOUT_MS = 5000;
function createFallbackConfig() {
    return envSchema.parse({
        ENVIRONMENT: fallbackEnvironment,
        SHINE_AI_WEB_URL: fallbackWebUrl,
        SHINE_AI_API_URL: fallbackApiUrl,
        SHINE_AI_FRONTEND_URL: getFrontendUrl(),
    });
}
function buildEnvironmentFromRuntime(payload) {
    const env = normalizeEnvironment(payload?.ENVIRONMENT);
    const defaults = getDefaultEndpoints(env);
    const candidate = {
        ENVIRONMENT: env,
        SHINE_AI_WEB_URL: payload?.SHINE_AI_WEB_URL || defaults.SHINE_AI_WEB_URL || fallbackWebUrl,
        SHINE_AI_API_URL: payload?.SHINE_AI_API_URL || defaults.SHINE_AI_API_URL || fallbackApiUrl,
        SHINE_AI_FRONTEND_URL: getFrontendUrl(),
    };
    const parsed = envSchema.safeParse(candidate);
    if (parsed.success) {
        return parsed.data;
    }
    console.warn('Invalid runtime configuration payload, falling back to defaults', 'error' in parsed ? parsed.error : undefined);
    return createFallbackConfig();
}
function applyConfig(config, resolve) {
    configState.config = config;
    configState.isLoaded = true;
    configState.isLoading = false;
    configState.loadPromise = null;
    resolve?.();
}
function waitForRuntimeConfig(resolve) {
    const start = Date.now();
    const poll = () => {
        if (typeof window !== 'undefined' && window.RUNTIME_CONFIG) {
            const config = buildEnvironmentFromRuntime(window.RUNTIME_CONFIG);
            applyConfig(config, resolve);
            return;
        }
        if (Date.now() - start >= CONFIG_LOAD_TIMEOUT_MS) {
            console.warn('Timed out waiting for runtime config, using fallback values');
            applyConfig(createFallbackConfig(), resolve);
            return;
        }
        window.setTimeout(poll, 50);
    };
    poll();
}
function getExistingConfigScript() {
    if (typeof document === 'undefined') {
        return null;
    }
    return document.querySelector(`script[${CONFIG_SCRIPT_ATTR}]`);
}
function injectConfigScript() {
    if (typeof document === 'undefined' || !document.head) {
        throw new Error('Cannot inject runtime config script without a document');
    }
    const script = document.createElement('script');
    script.src = getConfigUrl();
    script.async = true;
    script.setAttribute(CONFIG_SCRIPT_ATTR, 'true');
    document.head.appendChild(script);
    return script;
}
// Function to load configuration from config.js
function loadConfig() {
    if (configState.isLoaded) {
        return Promise.resolve();
    }
    if (configState.isLoading && configState.loadPromise) {
        return configState.loadPromise;
    }
    if (typeof window === 'undefined') {
        applyConfig(createFallbackConfig());
        return Promise.resolve();
    }
    configState.isLoading = true;
    configState.loadPromise = new Promise((resolve) => {
        if (window.RUNTIME_CONFIG) {
            applyConfig(buildEnvironmentFromRuntime(window.RUNTIME_CONFIG), resolve);
            return;
        }
        const existingScript = getExistingConfigScript();
        if (existingScript) {
            waitForRuntimeConfig(resolve);
            return;
        }
        const script = injectConfigScript();
        script.addEventListener('load', () => waitForRuntimeConfig(resolve), { once: true });
        script.addEventListener('error', () => {
            console.warn('Failed to load runtime config from', script.src);
            applyConfig(createFallbackConfig(), resolve);
        }, { once: true });
    });
    return configState.loadPromise;
}
// Export a uniform way to access environment variables
export const environment = new Proxy({}, {
    get: function (target, prop) {
        const key = prop;
        // For SHINE_AI_FRONTEND_URL, we can return immediately as it's compiled in
        if (key === 'SHINE_AI_FRONTEND_URL') {
            const frontendUrl = getFrontendUrl();
            return frontendUrl;
        }
        // For other properties, we need to ensure config.js is loaded
        // This returns a promise, but TypeScript's Proxy doesn't support async getters
        // So we'll return a default value based on the property
        if (!configState.isLoaded) {
            console.warn(`Attempting to access ${String(key)} before configuration is loaded`);
            // Start loading config if not already loading
            if (!configState.isLoading) {
                loadConfig();
            }
            // Return a default value based on the property
            if (key === 'SHINE_AI_WEB_URL') {
                return fallbackWebUrl;
            }
            else if (key === 'SHINE_AI_API_URL') {
                return fallbackApiUrl;
            }
            else if (key === 'ENVIRONMENT') {
                return fallbackEnvironment;
            }
            // For other properties, return undefined
            return undefined;
        }
        // Configuration is loaded, return the value
        if (configState.config) {
            const value = configState.config[key];
            return value;
        }
        return undefined;
    }
});
// Export a function to ensure configuration is loaded before proceeding
export async function ensureConfigLoaded() {
    return loadConfig();
}
